import React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import clsx from 'clsx';
import {mdiInformationOutline} from '@mdi/js';
import {
  Divider,
  Typography,
  Button,
  Drawer,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Box,
} from '@mui/material';
import {mdiPlus} from '@mdi/js';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Icon from '@mdi/react';
import Card from '../CommonComponents/Card';

import {DialogDelete} from '../CommonComponents/DialogBox';
import Snackbar from '../CommonComponents/Snackbar';
import {MAX_OUTPUT_FILES} from '../../ApplicationConfig/constants';
import { useTranslation } from "react-i18next";
import ModifyFile from './ModifyFile';
import _ from "lodash";
import AddSupportingFiles from "./AddSupportingFiles";

const PREFIX = 'FilesList';

const classes = {
  addBtn: `${PREFIX}-addBtn`,
  drawer: `${PREFIX}-drawer`,
  alert: `${PREFIX}-alert`,
  legendInfoIcon: `${PREFIX}-legendInfoIcon`,
  filePath: `${PREFIX}-filePath`,
  filePathItem: `${PREFIX}-filePathItem`,
  errorText: `${PREFIX}-errorText`,
  mb3: `${PREFIX}-mb3`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(({theme}) => ({
  [`& .${classes.addBtn}`]: {
    'marginTop': theme.spacing(-1),
    'borderStyle': 'dashed',
    'width': '100%',
    'borderColor': alpha(theme.palette.common.black, 0.23),
    '&.MuiButton-outlinedPrimary:hover': {
      borderStyle: 'dashed',
    },
  },

  [`& .${classes.alert}`]: {
    '& .MuiAlert-action': {
      alignItems: 'start',
    },
  },

  [`& .${classes.legendInfoIcon}`]: {
    marginTop: theme.spacing(-1.25),
    marginLeft: theme.spacing(1),
  },

  [`& .${classes.filePath}`]: {
    display: 'flex',
    flexFlow: 'wrap',
    alignItems: 'flex-end',
  },

  [`& .${classes.filePathItem}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.errorText}`]: {
    color: theme.palette.error.main,
  },

  [`& .${classes.mb3}`]: {
    marginBottom: theme.spacing(3),
  }
}));

export default function FilesList(props) {

  const { t } = useTranslation();
  const [state, setState] = React.useState({
    open: false,
    editIndex: null
  });
  const [dialogState, setDialogState] = React.useState({
    dialogDelete:false,
    fn: null
  });
  const [collapse, setCollapse] = React.useState(true);
  const [snackbarState, setSnackbarState] = React.useState({
    open: false,
    severity: "success",
    message: "",
    snackbarDelete: false,
    snackbarOutput: false,
  })

  const yesAnswer = t("requestForm.yes");
  const noAnswer = t("requestForm.no");

  const handleRemoveErrors=(e)=>{
    if(Boolean(props.errors.filesList?.[e.target.name]) && e.target.value!==""){
      let errs = _.cloneDeep(props.errors);
      delete errs.filesList[e.target.name];
      props.setErrors(errs);
    }
  }

  const handleValueChange = (e)=>{
    props.setFormData({
      ...props.formData,
      [e.target.name]: e.target.value
    });

    handleRemoveErrors(e);
  };

  const toggleDrawer = (open)=> {
    setState({...state, open: open});
  };

  const handleEditOutputFile =(i)=>{
    setState({...state, 
      open: true,
      editIndex: i
    });
  };

  const handleAddNewOutput=()=>{
    setState({...state, 
      open: true,
      editIndex: null
    });
  }

  const handleCloseDeleteDialog=()=>{
    setDialogState({
      ...dialogState,
      dialogDelete:false,
      fn:null
    });
  }

  const handleCloseSnackbarDelete=()=>{
    setSnackbarState({...snackbarState, snackbarDelete: false, message: ""});
  }

  const deleteOutputFile = (index)=>{
    const newOutputList = props.formData.outputFiles.slice(0, index).concat(props.formData.outputFiles.slice(index+1));
    for(let i = 0; i<newOutputList.length; i++){
      newOutputList[i].id = (i+1).toString();
    }

    let newData = _.cloneDeep(props.formData);
    newData.outputFiles = newOutputList;

    props.setFormData(newData);
    handleCloseDeleteDialog();

    //clear errors
    let errs=_.cloneDeep(props.errors);
    if(props.errors.filesList){
      Object.keys(props.errors.filesList).forEach(k=>{
        const match = k.match(/\d+/);
        if(match && k.startsWith("outputFiles.")){
          const j = Number(match[0]);
          if(j===index){
            delete errs.filesList[k];
          }else if(j>index){
            delete errs.filesList[k];
            errs.filesList[k.replace(/\d+(?!.*\d)/,j-1)]=props.errors.filesList[k];
          }
        }
      });
    }
    props.setErrors(errs);
    setSnackbarState({snackbarDelete: true, severity: "success", message: t("requestForm.outputDeleted")});
  };

  const handleAlertOpen = (index) => {
    handleCloseSnackbarDelete();

    setDialogState({
      ...dialogState,
      dialogDelete: true,
      fn:()=>deleteOutputFile(index)
    });
  };

  let errorCounts = {};
  props.formData.outputFiles.forEach((file, i)=>{
    if(props.errors.filesList){
      errorCounts[i]=Object.keys(props.errors.filesList).filter(e=>e.startsWith("outputFiles."+i)).length;
    }
  });

  const supportingFilesSection=(fileType, addBtnClass, tooltip,mandatorySection=true,extraParagraph=false)=>{
    let fileTypeDisplay=null;
    if(fileType.includes("other")){
      fileTypeDisplay="additionalDetails";
    }
    return <AddSupportingFiles 
      step="filesList"
      filesList={props.fileFolders.filesList}
      supportingFiles={props.formData.supportingFiles}
      fileType={fileType}
      fileTypeDisplay={fileTypeDisplay}
      readOnly={props.readOnly || props.analystEditFlag}
      errors={props.errors}
      setErrors={props.setErrors}
      handleUpdateSupportingFiles={(newVal)=>{
        props.setFormData({
          ...props.formData,
          supportingFiles: newVal
        });
      }}
      addBtnClass={addBtnClass}
      tooltip={tooltip}
      mandatorySection={mandatorySection}
      extraParagraph={extraParagraph}
    />
  }

  const handleAddNew = (values)=>{
    const newOutputList = props.formData.outputFiles.concat(values);
    props.setFormData({...props.formData, ...{outputFiles: newOutputList}});
    toggleDrawer(false);
    
    //DELAY ADDED SO SNACKBAR WILL BE ANNOUNCED AFTER DRAWER CLOSES
    setTimeout(function() {
      setSnackbarState({...snackbarState, snackbarOutput: true, severity: "success", message: t("requestForm.outputAdded")});
    }, 10);
  }

  const handleEditOutput=(index, values)=>{
    let newOutputList = [...props.formData.outputFiles];
    newOutputList[index] = values;
    props.setFormData({...props.formData, ...{outputFiles: newOutputList}});
    toggleDrawer(false);

    //DELAY ADDED SO SNACKBAR WILL BE ANNOUNCED AFTER DRAWER CLOSES
    setTimeout(function() {
      setSnackbarState({...snackbarState, snackbarOutput: true, severity: "success", message: t("requestForm.outputEdited")});
    }, 10);
  }

  return (
    <Root>
      <Typography component="h2" variant="h6" sx={{mb: 3}}>
        {t("requestForm.filesList.title")}
      </Typography>
      <Typography component="p" variant="body1" sx={{mb: 3}}>
        {t("requestForm.filesList.explanation")}
      </Typography>
      <Divider sx={{mb: 3}} />
      <Collapse in={collapse}>
        <Alert
          role="note"
          severity="info"
          className={classes.alert}
          sx={{mb: 3}}
          action={
            <IconButton
              aria-label={t('requestForm.close')}
              onClick={() => {
                setCollapse(false);
              }}
              size="large">
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Typography component="p" variant="body2" sx={{mb: 2}}>
            {t("requestForm.filesList.pleaseConsider")}
          </Typography>
          <Box sx={{mb: 2}}>
          <ul>
            <li>
              <Typography variant="body2">
                {t("requestForm.filesList.checkAgainstGuidelines")}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                {t("requestForm.filesList.deleteNotNeeded")}
              </Typography>
            </li>
          </ul>
          </Box>
          <Typography component="p" variant="body2">
            {t("requestForm.filesList.requestStored")}
          </Typography>
        </Alert>
      </Collapse>
      <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
        {t("requestForm.filesList.screeningQuestions")}
      </Typography>

      <Box sx={{mb: 2}}>
      <FormControl
        component="fieldset"
        required
        error={Boolean(props.errors.filesList?.consistentOutput)}
        aria-describedby="consistentOutput-helperText"
      >
        <FormLabel component="legend">
          {t("requestForm.filesList.consistentOutput")}
          <span className="screen-reader-text">{t("inputs.required")}</span>
        </FormLabel>
        <FormHelperText id="consistentOutput-helperText">{t(props.errors.filesList?.consistentOutput)}</FormHelperText>
        <RadioGroup 
          id="consistentOutput" 
          name="consistentOutput"
          onChange={(e)=>props.readOnly ? null : handleValueChange(e)}
          value={props.formData.consistentOutput}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio color="primary" />}
            label={yesAnswer}
          />
          <FormControlLabel
            value="No"
            control={<Radio color="primary" />}
            label={noAnswer}
          />
        </RadioGroup>
      </FormControl>
      </Box>
      <Box sx={{mb: 2}}>
      <FormControl
        component="fieldset"
        required
        error={Boolean(props.errors.filesList?.vettingRules)}
        aria-describedby="vettingRules-helperText"
      >
        <FormLabel component="legend">
          {t("requestForm.filesList.vettingRules")}
          <span className="screen-reader-text">{t("inputs.required")}</span>
        </FormLabel>
        <FormHelperText id="vettingRules-helperText">{t(props.errors.filesList?.vettingRules)}</FormHelperText>
        <RadioGroup 
          id="vettingRules" 
          name="vettingRules"
          onChange={(e)=>props.readOnly ? null : handleValueChange(e)}
          value={props.formData.vettingRules}
        >
          <FormControlLabel
            value="Yes"
            control={<Radio color="primary" />}
            label={yesAnswer}
          />
          <FormControlLabel
            value="No"
            control={<Radio color="primary" />}
            label={noAnswer}
          />
        </RadioGroup>
      </FormControl>
      </Box>
      <Grid container>
        <Grid xs item>
          <Box sx={{mb: 2}}>
          <FormControl
            component="fieldset"
            required
            sx={{mb: 2}}
            error={Boolean(props.errors.filesList?.finalOutput)}
            aria-describedby="finalOutput-helperText"
          >
            <FormLabel component="legend">
              {t("requestForm.filesList.finalOutput")}
              <span className="screen-reader-text">{t("inputs.required")}</span>
            </FormLabel>
            <FormHelperText id="finalOutput-helperText">{t(props.errors.filesList?.finalOutput)}</FormHelperText>
            <RadioGroup
              id="finalOutput"
              name="finalOutput"
              onChange={(e)=>props.readOnly ? null : handleValueChange(e)}
              value={props.formData.finalOutput}
            >
              <FormControlLabel
                value="Yes"
                control={<Radio color="primary" />}
                label={yesAnswer}
              />
              <FormControlLabel
                value="No"
                control={<Radio color="primary" />}
                label={noAnswer}
              />
            </RadioGroup>

          </FormControl>
          </Box>
        </Grid>
      </Grid>
      {props.formData.finalOutput === "No" &&
          <Alert
          role="note"
          severity="info"
          className={classes.alert}
          sx={{mb: 3}}

        >
          <Typography component="p" variant="body2" sx={{mb: 2}}>
            {t('requestForm.filesList.finalOutputGuidelines')}
          </Typography>
          <Typography component="p" variant="body2" sx={{mb: 2}}>
            {t('requestForm.filesList.finalOutputGuidelinesPara2')}
          </Typography>
        </Alert>
        }
      <Divider sx={{mb: 3}} />
      <Grid container>
        <Grid xs item>
          <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
            {t("requestForm.filesList.outputFiles")}
          </Typography>
        </Grid>
        <Grid component="span" className={classes.legendInfoIcon} item>
          <Tooltip title={t("requestForm.filesList.outputFilesTip")} arrow describeChild>
            <IconButton
              aria-label={t('requestForm.toolTipMoreInformation')}
              edge="end"
              size="large">
              <Icon path={mdiInformationOutline} size={1} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      <OutputFilesList 
        outputFiles={props.formData.outputFiles}
        listError={Boolean(props.errors.filesList?.outputFiles)}
        errorCounts={errorCounts}
        outputFilesFolder={props.formData.outputFilesFolder}
        handleEditOutputFile={handleEditOutputFile}
        handleAlertOpen={handleAlertOpen}
        handleAddNewOutput={handleAddNewOutput}
        readOnly={props.readOnly}
        analystEditFlag={props.analystEditFlag}
        isAnalyst={props.isAnalyst}
      />
      <Divider sx={{mb: 3, mt: 3}} />
      <Grid container>
        <Grid xs item>
          <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
            {t("requestForm.filesList.mandatorySupportingFiles")}
          </Typography>
        </Grid>
        <Grid component="span" className={classes.legendInfoIcon} item>
          <Tooltip title={t("requestForm.filesList.mandatorySupportingFilesTip")} arrow describeChild>
            <IconButton
              aria-label={t('requestForm.toolTipMoreInformation')}
              edge="end"
              size="large">
              <Icon path={mdiInformationOutline} size={1} />
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>
      {supportingFilesSection("syntaxFile", `${classes.addBtn} ${classes.mb3}`)}
      {supportingFilesSection("variableListFile", classes.addBtn, t("requestForm.filesList.variableListFileTip"))}
      <Divider sx={{mb: 3, mt: 3}} />
      <Typography component="h3" variant="subtitle2" sx={{mb: 3}}>
        {t("requestForm.filesList.additionalSupportingFiles")}
      </Typography>
      {supportingFilesSection("other", `${classes.addBtn} ${classes.mb3}`)}
      <ModifyFile 
        toggleDrawer={toggleDrawer} 
        formData={props.formData}
        errors={props.errors}
        setErrors={props.setErrors}
        filesList={props.fileFolders.filesList} 
        readOnly={props.readOnly} 
        analystEditFlag={props.analystEditFlag}
        isAnalyst={props.isAnalyst}
        index={state.editIndex}
        handleAddNew={handleAddNew}
        handleEditOutput={handleEditOutput}
        open={state.open}
      />
      <DialogDelete 
        toggleDialog={handleCloseDeleteDialog}
        handleDelete={dialogState.fn} 
        open={dialogState.dialogDelete} 
        snackbar={
          <Snackbar 
            message={snackbarState.message}
            severity={snackbarState.severity}
            open={snackbarState.snackbarDelete}
            handleClose={handleCloseSnackbarDelete}
          />
        }
      />
      <Snackbar 
        message={snackbarState.message}
        severity={snackbarState.severity}
        open={snackbarState.snackbarOutput}
        handleClose={()=>{setSnackbarState({...snackbarState, snackbarOutput: false, message: ""})}}
      />
    </Root>
  );
}
FilesList.propTypes={
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  setErrors: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
  isAnalyst: PropTypes.bool.isRequired,
  fileFolders: PropTypes.object.isRequired,
  requestStatus: PropTypes.number.isRequired,
};

function OutputFilesList(props){

  const { t } = useTranslation();
  const {
    outputFiles,
    listError,
    errorCounts,
    outputFilesFolder,
    handleEditOutputFile,
    handleAlertOpen,
    handleAddNewOutput,
    readOnly,
    analystEditFlag,
    isAnalyst
  } = props;
  
  return (
    <>
      <Typography id="output-file-header" variant="body2" className={listError ? classes.errorText : ""}>
        {t("requestForm.modifyFile.addOutputFileTitle")+"... "}<span aria-hidden="true">*</span>
        <span className="screen-reader-text">{t("inputs.required")}</span>
      </Typography>
      <Typography 
        variant="caption"
        color="textSecondary"
        component="p"
        sx={{mb: 2}}
        className={listError ? classes.errorText: ''}
      >
        {t("requestForm.modifyFile.addSupportingFilesOneFile")}
      </Typography>
      <Paper className="paper-grey" sx={{mb: 3}} variant="outlined">
        {outputFiles.length > 0 ?
        (outputFiles.map((file, i)=>{
          return (
            <Card
              key={`outputFile-${i}`}
              title={t("requestForm.modifyFile.addOutputFileCardTitle", {index: i+1})}
              titleComponent="h4"
              error={errorCounts[i] ? errorCounts[i]+" errors" : ""}
              primaryButton={isAnalyst ? t("requestForm.filesList.review") :
                (readOnly ? t("requestForm.filesList.view") : t("requestForm.filesList.edit"))
              }
              secondaryButton={t("requestForm.modifyFile.addSupportingFilesDelete")}
              primaryClick={()=>handleEditOutputFile(i)}
              secondaryClick={()=>handleAlertOpen(i)}
              readOnly={readOnly || analystEditFlag}
              isOutput={true}
              name={`outputFile-${i}`}
              content={
                <>
                  <Typography
                    variant="caption"
                    component="p"
                    color="textSecondary"
                  >
                    {t("requestForm.modifyFile.addSupportingFilesFilePath")}
                  </Typography>
                  <Box className={classes.filePath} sx={{mb: 2}}>
                    <div className={classes.filePathItem}>
                      <Typography 
                        id={`outputFile-${i}-fileName`} 
                        variant="body2" 
                        component="p"
                      >
                        {file.outputFile}
                      </Typography>
                    </div>
                  </Box>
                  {file.sheetName !== "" &&
                    <>
                      <Typography
                        variant="caption"
                        component="p"
                        color="textSecondary"
                      >
                        {t("requestForm.modifyFile.sheetName")}
                      </Typography>
                      <Typography 
                        id={`outputFile-${i}-sheetName`} 
                        variant="body2" 
                        component="p"
                      >
                        {file.sheetName}
                      </Typography>
                    </>
                  }
                </>
              }
            />
          )})
        ) : (
        <Typography variant="caption" component="p" color="textSecondary">
          {t("requestForm.modifyFile.addSupportingFilesNoOutput")}
        </Typography>
        )}
      </Paper>
      {!readOnly && !analystEditFlag &&
        <>
          {outputFiles.length < MAX_OUTPUT_FILES ?
            <Button
              aria-describedby="output-file-header"
              id="add-output-btn"
              variant="outlined"
              color="primary"
              startIcon={<Icon path={mdiPlus} size={1} />}
              fullWidth
              className={classes.addBtn}
              onClick={handleAddNewOutput}
              disabled={outputFilesFolder === ""}
            >
              {t("requestForm.modifyFile.addOutputFileButton")}
            </Button>
            :
            <Tooltip
              title={t("requestForm.filesList.maxOutputFiles")}
              arrow
              describeChild
            >
              <span>
                <Button
                  id="add-output-btn"
                  variant="outlined"
                  color="primary"
                  startIcon={<Icon path={mdiPlus} size={1} />}
                  fullWidth
                  className={classes.addBtn}
                  disabled={true}
                >
                  {t("requestForm.modifyFile.addOutputFileButton")}
                </Button>
              </span>
            </Tooltip>
          }
        </>
      }
    </>
  );
}
OutputFilesList.propTypes={
  outputFiles: PropTypes.arrayOf(PropTypes.object).isRequired,
  listError: PropTypes.bool.isRequired,
  errorCounts: PropTypes.object.isRequired,
  outputFilesFolder: PropTypes.string.isRequired,
  handleEditOutputFile: PropTypes.func.isRequired,
  handleAlertOpen: PropTypes.func.isRequired,
  handleAddNewOutput: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  analystEditFlag: PropTypes.bool.isRequired,
  isAnalyst: PropTypes.bool.isRequired,
};